/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Frontend SPA Contract API
 * Canonical API contract entrypoint.
 *
 * Source architecture:
 * - split by module / bounded context
 * - one file per aggregate root
 * - child entities remain owned by aggregate root
 * - cross-cutting technical concerns live under `openapi/common`
 *
 * OpenAPI spec version: 1.0.0
 */
import * as zod from "zod";

/**
 * Retrieve a paginated list of material offers.
 * @summary List material offers with optional filtering, sorting, and pagination
 */

export const listMaterialOffersQueryPerPageMax = 100;

export const listMaterialOffersQuerySortByDefault = `createdAt`;
export const listMaterialOffersQuerySortDirDefault = `asc`;

export const ListMaterialOffersQueryParams = zod.object({
  page: zod.coerce.number().int().min(1).describe("Page number (1-indexed)"),
  perPage: zod.coerce
    .number()
    .int()
    .min(1)
    .max(listMaterialOffersQueryPerPageMax)
    .describe("Number of items per page"),
  q: zod.coerce.string().optional().describe("Free-text search query"),
  sortBy: zod
    .enum([
      "materialId",
      "supplierId",
      "isPriceProtected",
      "minimumOrderQuantity",
      "orderQuantityMultiple",
      "createdAt",
      "editedAt",
    ])
    .default(listMaterialOffersQuerySortByDefault)
    .describe("Column to sort by."),
  sortDir: zod
    .enum(["asc", "desc"])
    .default(listMaterialOffersQuerySortDirDefault)
    .describe("Sort direction"),
  materialId: zod.uuid().optional().describe("Filter by material ID."),
  supplierId: zod.uuid().optional().describe("Filter by supplier ID."),
  isPriceProtected: zod.coerce.boolean().optional().describe("Filter by price-protected flag."),
  createdAfter: zod.iso
    .datetime({offset: true})
    .optional()
    .describe("Filter to material offers created after this timestamp."),
  createdBefore: zod.iso
    .datetime({offset: true})
    .optional()
    .describe("Filter to material offers created before this timestamp."),
});

export const listMaterialOffersResponseDataItemMaterialCodeRegExp = new RegExp("^[A-Z]{3}-\\d{3}$");

export const listMaterialOffersResponseMetaTotalMin = 0;

export const ListMaterialOffersResponse = zod.object({
  data: zod
    .array(
      zod.object({
        id: zod
          .string()
          .describe("Material-offer identifier composed as `{materialId}{supplierId}`."),
        createdAt: zod.iso.datetime({offset: true}).describe("Creation timestamp."),
        editedAt: zod.iso.datetime({offset: true}).describe("Last edit timestamp."),
        material: zod
          .object({
            id: zod.uuid().describe("Unique material identifier."),
            name: zod.string().describe("Material name."),
            code: zod
              .string()
              .regex(listMaterialOffersResponseDataItemMaterialCodeRegExp)
              .describe("Material code in LLL-DDD format."),
          })
          .describe("Compact material reference used by material component responses."),
        supplier: zod
          .object({
            id: zod.uuid().describe("Unique supplier identifier."),
            companyName: zod.string().describe("Company or organization name."),
            companyId: zod
              .string()
              .nullish()
              .describe("Official company registration or tax identifier."),
          })
          .describe("Compact supplier reference used by other engineering aggregates."),
        price: zod
          .union([
            zod
              .object({
                amount: zod
                  .string()
                  .describe("Decimal amount serialized as a string to preserve precision."),
                currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
              })
              .describe("Support-domain price value object returned by APIs."),
            zod.null(),
          ])
          .describe(
            "Supplier-specific material price composed by string amount plus currency code.",
          ),
        isPriceProtected: zod
          .boolean()
          .describe(
            "Whether this material offer has protected pricing. Typically follows material price-protection rules.",
          ),
        minimumOrderQuantity: zod
          .int()
          .min(1)
          .nullish()
          .describe("Minimum order quantity for this offer."),
        orderQuantityMultiple: zod
          .int()
          .min(1)
          .nullish()
          .describe("Required multiple for ordered quantities."),
      }),
    )
    .describe("Array of material offers."),
  meta: zod.object({
    total: zod
      .int()
      .min(listMaterialOffersResponseMetaTotalMin)
      .describe("Total count of items matching query ignoring pagination"),
    page: zod.int().min(1).describe("Current page number"),
    perPage: zod.int().min(1).describe("Number of items returned per page"),
  }),
});

/**
 * Create a new material offer linking a material and a supplier.
 * @summary Create a material offer
 */

export const CreateMaterialOfferBody = zod.object({
  materialId: zod.uuid().describe("Material ID being offered."),
  supplierId: zod.uuid().describe("Supplier ID offering the material."),
  price: zod
    .object({
      amount: zod
        .string()
        .optional()
        .describe("Decimal amount serialized as a string to preserve precision."),
      currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
    })
    .optional()
    .describe("Support-domain price request payload."),
  minimumOrderQuantity: zod
    .int()
    .min(1)
    .optional()
    .describe("Minimum order quantity for this offer."),
  orderQuantityMultiple: zod
    .int()
    .min(1)
    .optional()
    .describe("Required multiple for ordered quantities."),
});

export const createMaterialOfferResponseMaterialCodeRegExp = new RegExp("^[A-Z]{3}-\\d{3}$");

export const CreateMaterialOfferResponse = zod.object({
  id: zod.string().describe("Material-offer identifier composed as `{materialId}{supplierId}`."),
  createdAt: zod.iso.datetime({offset: true}).describe("Creation timestamp."),
  editedAt: zod.iso.datetime({offset: true}).describe("Last edit timestamp."),
  material: zod
    .object({
      id: zod.uuid().describe("Unique material identifier."),
      name: zod.string().describe("Material name."),
      code: zod
        .string()
        .regex(createMaterialOfferResponseMaterialCodeRegExp)
        .describe("Material code in LLL-DDD format."),
    })
    .describe("Compact material reference used by material component responses."),
  supplier: zod
    .object({
      id: zod.uuid().describe("Unique supplier identifier."),
      companyName: zod.string().describe("Company or organization name."),
      companyId: zod
        .string()
        .nullish()
        .describe("Official company registration or tax identifier."),
    })
    .describe("Compact supplier reference used by other engineering aggregates."),
  price: zod
    .union([
      zod
        .object({
          amount: zod
            .string()
            .describe("Decimal amount serialized as a string to preserve precision."),
          currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
        })
        .describe("Support-domain price value object returned by APIs."),
      zod.null(),
    ])
    .describe("Supplier-specific material price composed by string amount plus currency code."),
  isPriceProtected: zod
    .boolean()
    .describe(
      "Whether this material offer has protected pricing. Typically follows material price-protection rules.",
    ),
  minimumOrderQuantity: zod
    .int()
    .min(1)
    .nullish()
    .describe("Minimum order quantity for this offer."),
  orderQuantityMultiple: zod
    .int()
    .min(1)
    .nullish()
    .describe("Required multiple for ordered quantities."),
});

/**
 * Retrieve a material offer by ID.
 * @summary Get a material offer by ID
 */
export const getMaterialOfferResponseMaterialCodeRegExp = new RegExp("^[A-Z]{3}-\\d{3}$");

export const GetMaterialOfferResponse = zod.object({
  id: zod.string().describe("Material-offer identifier composed as `{materialId}{supplierId}`."),
  createdAt: zod.iso.datetime({offset: true}).describe("Creation timestamp."),
  editedAt: zod.iso.datetime({offset: true}).describe("Last edit timestamp."),
  material: zod
    .object({
      id: zod.uuid().describe("Unique material identifier."),
      name: zod.string().describe("Material name."),
      code: zod
        .string()
        .regex(getMaterialOfferResponseMaterialCodeRegExp)
        .describe("Material code in LLL-DDD format."),
    })
    .describe("Compact material reference used by material component responses."),
  supplier: zod
    .object({
      id: zod.uuid().describe("Unique supplier identifier."),
      companyName: zod.string().describe("Company or organization name."),
      companyId: zod
        .string()
        .nullish()
        .describe("Official company registration or tax identifier."),
    })
    .describe("Compact supplier reference used by other engineering aggregates."),
  price: zod
    .union([
      zod
        .object({
          amount: zod
            .string()
            .describe("Decimal amount serialized as a string to preserve precision."),
          currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
        })
        .describe("Support-domain price value object returned by APIs."),
      zod.null(),
    ])
    .describe("Supplier-specific material price composed by string amount plus currency code."),
  isPriceProtected: zod
    .boolean()
    .describe(
      "Whether this material offer has protected pricing. Typically follows material price-protection rules.",
    ),
  minimumOrderQuantity: zod
    .int()
    .min(1)
    .nullish()
    .describe("Minimum order quantity for this offer."),
  orderQuantityMultiple: zod
    .int()
    .min(1)
    .nullish()
    .describe("Required multiple for ordered quantities."),
});

/**
 * Replace the mutable material-offer fields with the provided payload.
 * @summary Update a material offer
 */

export const UpdateMaterialOfferBody = zod.object({
  price: zod
    .object({
      amount: zod
        .string()
        .optional()
        .describe("Decimal amount serialized as a string to preserve precision."),
      currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
    })
    .optional()
    .describe("Support-domain price request payload."),
  minimumOrderQuantity: zod
    .int()
    .min(1)
    .optional()
    .describe("Minimum order quantity for this offer."),
  orderQuantityMultiple: zod
    .int()
    .min(1)
    .optional()
    .describe("Required multiple for ordered quantities."),
});

export const updateMaterialOfferResponseMaterialCodeRegExp = new RegExp("^[A-Z]{3}-\\d{3}$");

export const UpdateMaterialOfferResponse = zod.object({
  id: zod.string().describe("Material-offer identifier composed as `{materialId}{supplierId}`."),
  createdAt: zod.iso.datetime({offset: true}).describe("Creation timestamp."),
  editedAt: zod.iso.datetime({offset: true}).describe("Last edit timestamp."),
  material: zod
    .object({
      id: zod.uuid().describe("Unique material identifier."),
      name: zod.string().describe("Material name."),
      code: zod
        .string()
        .regex(updateMaterialOfferResponseMaterialCodeRegExp)
        .describe("Material code in LLL-DDD format."),
    })
    .describe("Compact material reference used by material component responses."),
  supplier: zod
    .object({
      id: zod.uuid().describe("Unique supplier identifier."),
      companyName: zod.string().describe("Company or organization name."),
      companyId: zod
        .string()
        .nullish()
        .describe("Official company registration or tax identifier."),
    })
    .describe("Compact supplier reference used by other engineering aggregates."),
  price: zod
    .union([
      zod
        .object({
          amount: zod
            .string()
            .describe("Decimal amount serialized as a string to preserve precision."),
          currency: zod.enum(["USD", "EUR", "GBP"]).describe("Supported currency code."),
        })
        .describe("Support-domain price value object returned by APIs."),
      zod.null(),
    ])
    .describe("Supplier-specific material price composed by string amount plus currency code."),
  isPriceProtected: zod
    .boolean()
    .describe(
      "Whether this material offer has protected pricing. Typically follows material price-protection rules.",
    ),
  minimumOrderQuantity: zod
    .int()
    .min(1)
    .nullish()
    .describe("Minimum order quantity for this offer."),
  orderQuantityMultiple: zod
    .int()
    .min(1)
    .nullish()
    .describe("Required multiple for ordered quantities."),
});

/**
 * Delete a material offer by ID.
 * @summary Delete a material offer
 */
export const DeleteMaterialOfferResponse = zod.void();
